import React from "react";
import moment from "moment";
import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import ParticipacaoEmEmpresas from "../../common/query/ParticipacaoEmEmpresas";
import Enderecos from "../../common/query/Enderecos";
import Emails from "../../common/query/Emails";
import Telefones from "../../common/query/Telefones";
import { countDadosCadastrais, formatDocument } from "../../../utils/utils";
import { FiPhone, FiUserCheck } from "react-icons/fi";
import { LuBuilding2 } from "react-icons/lu";
import { HiOutlineLocationMarker } from "react-icons/hi";
import { MdOutlineEmail } from "react-icons/md";

const buildDurationLabel = (fromMoment, suffix = "") => {
  if (!fromMoment || !fromMoment.isValid()) return null;
  const years = moment().diff(fromMoment, "years");
  const afterYears = fromMoment.clone().add(years, "years");
  const months = moment().diff(afterYears, "months");
  const parts = [];
  if (years) parts.push(`${years} ano${years === 1 ? "" : "s"}`);
  if (months) parts.push(`${months} mês${months === 1 ? "" : "es"}`);
  if (!parts.length) return `menos de 1 mês${suffix ? ` ${suffix}` : ""}`;
  const joined = parts.join(" e ");
  return suffix ? `${joined} ${suffix}` : joined;
};

const CadastralPremiumPFQuery = ({ data, dataFull }) => {
  const totalDadosCadastrais = Array.isArray(dataFull)
    ? dataFull.reduce((acc, item) => acc + countDadosCadastrais(item), 0)
    : countDadosCadastrais(dataFull);

  // Só renderiza se houver exatamente 1
  const podeRenderizarDadosCadastrais = totalDadosCadastrais < 2;
  const cadastro = data?.dadosCadastrais || {};
  const birthMoment = cadastro.dataNascimento
    ? moment(cadastro.dataNascimento, ["DD/MM/YYYY", "YYYY-MM-DD"], true)
    : null;
  const birthLabel =
    birthMoment && birthMoment.isValid()
      ? birthMoment.format("DD/MM/YYYY")
      : null;
  const birthAgeLabel = buildDurationLabel(birthMoment);
  const statusLabel = cadastro.situacao || "";
  const summaryCards = [
    {
      label: "CPF",
      value: formatDocument(cadastro.cpf) || "Não disponível",
      hint: "Documento",
    },
    {
      label: "Nome da mãe",
      value: cadastro.nomeMae || "Não informado",
      hint: "Parente",
    },
    {
      label: "Sexo",
      value: cadastro?.sexo || "Não informado",
      hint: "Gênero",
    },
    {
      label: "Nascimento",
      value: birthLabel || "Não informado",
      hint: "Data",
    },
  ];

  return (
    <div>
      {podeRenderizarDadosCadastrais && (
        <>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <FiUserCheck className="text-blue-700" />{" "}
              <span>Dados Cadastrais</span>
            </div>
          </TitleStripe>
          <div className="mt-4 text-xs">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                  Nome
                </p>
                <p className="text-xl font-semibold text-gray-900">
                  {cadastro.nome || "Indisponível"}
                </p>
              </div>
            </div>

            <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                Situação: {statusLabel || "Desconhecida"}
              </span>
              {birthLabel && (
                <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                  Nasc: {birthLabel}
                </span>
              )}
              {birthAgeLabel && (
                <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                  {birthAgeLabel}
                </span>
              )}
            </div>

            <div className="mt-3 grid gap-2 grid-cols-4">
              {summaryCards.map((item) => (
                <div
                  key={item.label}
                  className="rounded-xl border border-gray-200 bg-slate-50 px-3 py-2"
                >
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                    {item.label}
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {item.value}
                  </p>
                  <p className="text-[10px] text-gray-500">{item.hint}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {data?.participacaoEmEmpresas && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <LuBuilding2 className="text-blue-700" />
              <span>Participação em empresas</span>
            </div>
          </TitleStripe>
          {data?.participacaoEmEmpresas?.length > 0 ? (
            <ParticipacaoEmEmpresas empresas={data?.participacaoEmEmpresas} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.enderecos && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <HiOutlineLocationMarker className="text-blue-700" />{" "}
              <span>Histórico de Endereços</span>
            </div>
          </TitleStripe>
          {data?.enderecos?.length > 0 ? (
            <Enderecos enderecos={data?.enderecos} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.emails && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <MdOutlineEmail className="text-blue-700" />{" "}
              <span>Histórico de E-mails</span>
            </div>
          </TitleStripe>
          {data?.emails?.length > 0 ? (
            <Emails emails={data?.emails} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.telefones && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <FiPhone className="text-blue-700" />{" "}
              <span>Histórico de Telefones</span>
            </div>
          </TitleStripe>
          {data?.telefones?.length > 0 ? (
            <Telefones telefones={data?.telefones} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}
    </div>
  );
};

export default CadastralPremiumPFQuery;
